import { SceneWithUserNames } from '../../../../types/scene.types';
import { compareSceneCodes, compareChineseText, parseSceneCode, SceneCodeParts } from './SceneCodeUtils';

/**
 * 排序策略接口
 * 定义不同字段的排序比较逻辑
 */
export interface SortStrategy {
  /**
   * 比较两个场景
   * @param a 场景A
   * @param b 场景B
   * @param parseCache 解析结果缓存（可选，用于场景号排序优化）
   * @returns 比较结果：负数表示 a < b，正数表示 a > b，0 表示相等
   */
  compare(a: SceneWithUserNames, b: SceneWithUserNames, parseCache?: Map<string, SceneCodeParts | null>): number;
  
  /**
   * 检查值是否为空
   * @param value 要检查的值
   * @returns 是否为空
   */
  isEmpty(value: any): boolean;
  
  /**
   * 是否需要预解析缓存
   * @returns 是否需要缓存
   */
  needsCache(): boolean;
}

/**
 * 场景号排序策略
 * 使用场景号解析和比较逻辑，支持解析结果缓存
 * 当场号相同时，按创建时间（从远到近）排序
 */
export class SceneCodeSortStrategy implements SortStrategy {
  compare(a: SceneWithUserNames, b: SceneWithUserNames, parseCache?: Map<string, SceneCodeParts | null>): number {
    const codeComparison = compareSceneCodes(a, b, parseCache);
    
    // 如果场号相同（返回0），则按创建时间排序（从远到近）
    if (codeComparison === 0) {
      const timeA = a.sceneCreateTime ? new Date(a.sceneCreateTime).getTime() : 0;
      const timeB = b.sceneCreateTime ? new Date(b.sceneCreateTime).getTime() : 0;
      
      // 从远到近排序：时间小的（更早的）排在前面
      if (timeA !== timeB) {
        return timeA - timeB;
      }
      
      // 如果创建时间也相同，保持原顺序
      return 0;
    }
    
    return codeComparison;
  }
  
  isEmpty(value: any): boolean {
    return !value || value.trim() === '';
  }
  
  needsCache(): boolean {
    return true;
  }
}

/**
 * 中文文本排序策略
 * 使用中文拼音排序逻辑
 * 当按场景、拍摄地点、排期排序时，如果值相同，则按场号进行二级排序（场号永远正序）
 */
export class ChineseTextSortStrategy implements SortStrategy {
  compare(a: SceneWithUserNames, b: SceneWithUserNames, parseCache?: Map<string, SceneCodeParts | null>): number {
    // 中文文本排序不需要缓存，直接比较字段值
    const valueA = (a as any)[this.sortKey] || '';
    const valueB = (b as any)[this.sortKey] || '';
    return compareChineseText(valueA, valueB);
  }
  
  isEmpty(value: any): boolean {
    return !value || value.trim() === '' || value === '选择或创建场景';
  }
  
  needsCache(): boolean {
    // 当按场景、拍摄地点、排期排序时，需要缓存来支持二级排序
    return this.sortKey === 'sceneSet' || this.sortKey === 'sceneLocation' || this.sortKey === 'scenePeriod';
  }
  
  constructor(private sortKey: string) {}
}

/**
 * 排序策略工厂
 * 根据排序键返回对应的排序策略
 */
export class SortStrategyFactory {
  /**
   * 获取排序策略
   * @param sortKey 排序键
   * @returns 排序策略
   */
  static getStrategy(sortKey: string): SortStrategy {
    if (sortKey === 'sceneCode') {
      return new SceneCodeSortStrategy();
    }
    return new ChineseTextSortStrategy(sortKey);
  }
}

/**
 * 通用排序函数（使用策略模式）
 * 支持解析结果缓存优化，提升大数据量排序性能
 * 
 * @param scenes 场次行列表
 * @param sortKey 排序键
 * @param direction 排序方向
 * @returns 排序后的场景列表
 */
export const sortScenesWithStrategy = (
  scenes: SceneWithUserNames[],
  sortKey: string,
  direction: 'asc' | 'desc'
): SceneWithUserNames[] => {
  const strategy = SortStrategyFactory.getStrategy(sortKey);

  // 创建临时缓存（仅在本次排序期间有效）
  // 当按场景号排序，或按场景、拍摄地点、排期排序（需要二级排序）时，预解析所有场景号并缓存
  const parseCache = strategy.needsCache() ? new Map<string, SceneCodeParts | null>() : undefined;

  // 预解析所有场景号并缓存（当需要缓存时）
  if (parseCache) {
    scenes.forEach(scene => {
      const code = scene.sceneCode;
      if (code && !parseCache.has(code)) {
        parseCache.set(code, parseSceneCode(code));
      }
    });
  }
  
  return [...scenes].sort((a, b) => {
    // 获取要比较的值
    const valueA = sortKey === 'sceneCode' ? a.sceneCode : (a as any)[sortKey];
    const valueB = sortKey === 'sceneCode' ? b.sceneCode : (b as any)[sortKey];
    
    // 处理空值：空值始终排在最后，无论正序还是倒序
    const isEmptyA = strategy.isEmpty(valueA);
    const isEmptyB = strategy.isEmpty(valueB);
    
    if (isEmptyA && isEmptyB) return 0;  // 都为空，保持原顺序
    if (isEmptyA) return 1;  // a为空，排在后面
    if (isEmptyB) return -1; // b为空，排在后面
    
    // 先比较主排序字段
    const mainCompare = sortKey === 'sceneCode' 
      ? compareSceneCodes(a, b, parseCache)
      : compareChineseText(valueA, valueB);
    
    // 如果主排序字段值不同，按主排序方向排序
    if (mainCompare !== 0) {
      return direction === 'desc' ? -mainCompare : mainCompare;
    }
    
    // 如果主排序字段值相同，且排序键是场景、拍摄地点或排期，则按场号进行二级排序
    // 场号二级排序永远正序，不受主排序方向影响
    if (sortKey === 'sceneSet' || sortKey === 'sceneLocation' || sortKey === 'scenePeriod') {
      if (parseCache) {
        return compareSceneCodes(a, b, parseCache);
      } else {
        // 如果没有缓存，创建临时缓存
        const tempCache = new Map<string, SceneCodeParts | null>();
        return compareSceneCodes(a, b, tempCache);
      }
    }
    
    // 其他情况保持原顺序
    return 0;
  });
};

